// ─── src/components/dashboard/EditProposalModal.tsx ──────────────────────────
//
// Admin edit for a pending meetup proposal. Runs BEFORE approval — the
// backend rejects the PATCH if `status !== 'pending'` (already approved
// proposals must be edited on the resulting event instead).

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { MeetupProposal } from "../../types/meetupProposal";
import type { UpdateMeetupProposalPayload } from "../../api/meetupProposals";
import { PublicChip, PublicFieldsLegend } from "./PublicFieldChip";

interface Props {
  proposal: MeetupProposal;
  onClose: () => void;
  onSave: (payload: UpdateMeetupProposalPayload) => Promise<void>;
  saving: boolean;
}

function todayIso() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

const EMPTY_TO_UNDEFINED = (s: string) => s.trim() || undefined;

export function EditProposalModal({ proposal, onClose, onSave, saving }: Props) {
  // Local draft. Kept as strings (form-friendly) even for numeric-looking
  // fields like `expectedAttendees` — the DB stores them as strings anyway.
  const [form, setForm] = useState({
    eventTitle: proposal.eventTitle ?? "",
    targetAudience: proposal.targetAudience ?? "",
    eventFormat: proposal.eventFormat ?? "",
    eventFormatOther: proposal.eventFormatOther ?? "",
    motivation: proposal.motivation ?? "",
    agenda: proposal.agenda ?? "",
    structure: proposal.structure ?? "",
    seriesDetails: proposal.seriesDetails ?? "",
    speakers: proposal.speakers ?? "",
    proposedDate: proposal.proposedDate ?? "",
    startTime: proposal.startTime ?? "",
    durationHours: proposal.durationHours ?? "",
    venue: proposal.venue ?? "",
    venueStatus: proposal.venueStatus ?? "",
    mode: proposal.mode ?? "",
    meetingUrl: proposal.meetingUrl ?? "",
    expectedAttendees: proposal.expectedAttendees ?? "",
    otherDetails: proposal.otherDetails ?? "",
    hostGovernorate: proposal.hostGovernorate ?? "",
    hostCityArea: proposal.hostCityArea ?? "",
    coHostName: proposal.coHostName ?? "",
    coHostEmail: proposal.coHostEmail ?? "",
    additionalCoHosts: proposal.additionalCoHosts ?? "",
    partnerSupport: proposal.partnerSupport ?? "",
    partnershipDetails: proposal.partnershipDetails ?? "",
    logisticsSupport: proposal.logisticsSupport ?? "",
    wantsFacebookEvent: proposal.wantsFacebookEvent,
  });
  const [error, setError] = useState<string | null>(null);

  // Reset if the parent hands us a different proposal.
  useEffect(() => {
    setForm({
      eventTitle: proposal.eventTitle ?? "",
      targetAudience: proposal.targetAudience ?? "",
      eventFormat: proposal.eventFormat ?? "",
      eventFormatOther: proposal.eventFormatOther ?? "",
      motivation: proposal.motivation ?? "",
      agenda: proposal.agenda ?? "",
      structure: proposal.structure ?? "",
      seriesDetails: proposal.seriesDetails ?? "",
      speakers: proposal.speakers ?? "",
      proposedDate: proposal.proposedDate ?? "",
      startTime: proposal.startTime ?? "",
      durationHours: proposal.durationHours ?? "",
      venue: proposal.venue ?? "",
      venueStatus: proposal.venueStatus ?? "",
      mode: proposal.mode ?? "",
      meetingUrl: proposal.meetingUrl ?? "",
      expectedAttendees: proposal.expectedAttendees ?? "",
      otherDetails: proposal.otherDetails ?? "",
      hostGovernorate: proposal.hostGovernorate ?? "",
      hostCityArea: proposal.hostCityArea ?? "",
      coHostName: proposal.coHostName ?? "",
      coHostEmail: proposal.coHostEmail ?? "",
      additionalCoHosts: proposal.additionalCoHosts ?? "",
      partnerSupport: proposal.partnerSupport ?? "",
      partnershipDetails: proposal.partnershipDetails ?? "",
      logisticsSupport: proposal.logisticsSupport ?? "",
      wantsFacebookEvent: proposal.wantsFacebookEvent,
    });
    setError(null);
  }, [proposal]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const buildPatch = (): UpdateMeetupProposalPayload | null => {
    if (form.proposedDate && form.proposedDate < todayIso()) {
      setError("Proposed date can't be in the past.");
      return null;
    }
    // Diff each field so we only send what changed.
    const patch: UpdateMeetupProposalPayload = {};
    const before = proposal as unknown as Record<string, string | boolean | null | undefined>;
    const after = form as unknown as Record<string, string | boolean | null | undefined>;

    // String fields — track by key name.
    const stringKeys: Array<keyof UpdateMeetupProposalPayload> = [
      "eventTitle", "targetAudience", "eventFormat", "eventFormatOther",
      "motivation", "agenda", "structure", "seriesDetails", "speakers",
      "proposedDate", "startTime", "durationHours", "venue", "venueStatus", "mode", "meetingUrl",
      "expectedAttendees", "otherDetails",
      "hostGovernorate", "hostCityArea",
      "coHostName", "coHostEmail", "additionalCoHosts",
      "partnerSupport", "partnershipDetails", "logisticsSupport",
    ];
    for (const k of stringKeys) {
      const b = (before[k] ?? "") as string;
      const a = (after[k] ?? "") as string;
      if (a.trim() !== b.trim()) {
        (patch as Record<string, unknown>)[k] = EMPTY_TO_UNDEFINED(a);
      }
    }

    if (form.wantsFacebookEvent !== proposal.wantsFacebookEvent) {
      patch.wantsFacebookEvent = form.wantsFacebookEvent;
    }
    return patch;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const patch = buildPatch();
    if (!patch) return;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await onSave(patch);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(" · ") : msg ?? "Save failed. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div>
            <p className="text-white font-bold text-lg">Edit proposal</p>
            <p className="text-brand-muted text-xs">
              Admin edit while the proposal is still pending. Once approved, edit the
              event instead.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Mixed public/internal fields — same signaling as the public
              hosting form: legend + chip on each publicly-visible field. */}
          <PublicFieldsLegend />

          <Field publicField label="Title" value={form.eventTitle} onChange={(v) => set("eventTitle", v)} />
          <Field publicField label="Target audience" value={form.targetAudience} onChange={(v) => set("targetAudience", v)} multiline rows={2} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field publicField label="Format" value={form.eventFormat} onChange={(v) => set("eventFormat", v)} />
            <Field publicField label="Format (other)" value={form.eventFormatOther} onChange={(v) => set("eventFormatOther", v)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field publicField label="Date" type="date" value={form.proposedDate} onChange={(v) => set("proposedDate", v)} min={todayIso()} />
            <Field publicField label="Start time" type="time" value={form.startTime} onChange={(v) => set("startTime", v)} />
            <Field publicField label="Duration (h)" value={form.durationHours} onChange={(v) => set("durationHours", v)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field publicField label="Governorate" value={form.hostGovernorate} onChange={(v) => set("hostGovernorate", v)} />
            <Field publicField label="City / area" value={form.hostCityArea} onChange={(v) => set("hostCityArea", v)} />
          </div>

          <Field publicField label="Venue" value={form.venue} onChange={(v) => set("venue", v)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field publicField label="Mode (in_person / online / hybrid)" value={form.mode} onChange={(v) => set("mode", v)} />
            <Field label="Meeting link (private)" value={form.meetingUrl} onChange={(v) => set("meetingUrl", v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Venue status" value={form.venueStatus} onChange={(v) => set("venueStatus", v)} />
            <Field publicField label="Expected attendees" value={form.expectedAttendees} onChange={(v) => set("expectedAttendees", v)} type="number" min="0" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field publicField label="Co-host name" value={form.coHostName} onChange={(v) => set("coHostName", v)} />
            <Field label="Co-host email" value={form.coHostEmail} onChange={(v) => set("coHostEmail", v)} />
          </div>
          <Field label="Additional co-hosts" value={form.additionalCoHosts} onChange={(v) => set("additionalCoHosts", v)} multiline rows={2} />

          <Field publicField label="Motivation" value={form.motivation} onChange={(v) => set("motivation", v)} multiline rows={4} />
          <Field publicField label="Agenda" value={form.agenda} onChange={(v) => set("agenda", v)} multiline rows={4} />
          <Field label="Structure" value={form.structure} onChange={(v) => set("structure", v)} />
          <Field label="Series details" value={form.seriesDetails} onChange={(v) => set("seriesDetails", v)} multiline rows={2} />
          <Field publicField label="Speakers" value={form.speakers} onChange={(v) => set("speakers", v)} multiline rows={3} />
          <Field label="Other details" value={form.otherDetails} onChange={(v) => set("otherDetails", v)} multiline rows={2} />

          <Field label="Partner support" value={form.partnerSupport} onChange={(v) => set("partnerSupport", v)} />
          <Field label="Partnership details" value={form.partnershipDetails} onChange={(v) => set("partnershipDetails", v)} multiline rows={3} />
          <Field label="Logistics support" value={form.logisticsSupport} onChange={(v) => set("logisticsSupport", v)} multiline rows={2} />

          <div>
            <span className="block text-brand-muted text-xs uppercase tracking-wider mb-1.5">
              Wants Facebook event
            </span>
            <div className="flex gap-2">
              {[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => set("wantsFacebookEvent", opt.value)}
                  className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${
                    form.wantsFacebookEvent === opt.value
                      ? "border-brand-green bg-brand-green/10 text-brand-green"
                      : "border-brand-border text-brand-muted hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-brand-border">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Small input used across the modal ────────────────────────────────────────

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  multiline?: boolean;
  rows?: number;
  min?: string;
  /** Render the shared "عام" chip next to the label. */
  publicField?: boolean;
}

function Field({ label, value, onChange, type = "text", multiline, rows, min, publicField }: FieldProps) {
  const base = "w-full rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-white text-sm focus:border-brand-green focus:outline-none";
  return (
    <label className="block">
      <span className="block text-brand-muted text-xs uppercase tracking-wider mb-1.5">
        {label}
        {publicField && <PublicChip />}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows ?? 3}
          className={base}
          dir="auto"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          className={base}
          dir="auto"
        />
      )}
    </label>
  );
}
